import { SUSPENSE } from '@react-rxjs/core'
import {
  filter,
  map,
  MonoTypeOperatorFunction,
  Observable,
  ObservableInput,
  ObservedValueOf,
  of,
  OperatorFunction,
  retry,
  switchMap,
  tap,
  timer,
} from 'rxjs'

/**
 * Projects each source value to an Observable when Result/Either is `Right`
 */
export const switchMapRight = <
  T extends { _tag: 'Left' | 'Right' },
  O extends ObservableInput<unknown>,
>(
  project: (value: Extract<T, { _tag: 'Right' }>, index: number) => O,
): OperatorFunction<T, Extract<T, { _tag: 'Left' }> | ObservedValueOf<O>> =>
  switchMap((value, index) =>
    value._tag === 'Left'
      ? of(value as Extract<T, { _tag: 'Left' }>)
      : project(value as Extract<T, { _tag: 'Right' }>, index),
  )

/**
 * Projects each source value to an Observable when Result/Either is `Left`
 */
export const switchMapLeft = <
  T extends { _tag: 'Left' | 'Right' },
  O extends ObservableInput<unknown>,
>(
  project: (value: Extract<T, { _tag: 'Left' }>, index: number) => O,
): OperatorFunction<T, Extract<T, { _tag: 'Right' }> | ObservedValueOf<O>> =>
  switchMap((value, index) =>
    value._tag === 'Left'
      ? project(value as Extract<T, { _tag: 'Left' }>, index)
      : of(value as Extract<T, { _tag: 'Right' }>),
  )

/**
 * Applies a given `project` function to each value emitted by the source Observable
 * when emitted Result/Either is `Right`
 */
export const mapRight = <T extends { _tag: 'Left' | 'Right' }, R>(
  project: (value: Extract<T, { _tag: 'Right' }>, index: number) => R,
): OperatorFunction<T, Extract<T, { _tag: 'Left' }> | R> =>
  map((value, index) =>
    value._tag === 'Left'
      ? (value as Extract<T, { _tag: 'Left' }>)
      : project(value as Extract<T, { _tag: 'Right' }>, index),
  )

/**
 * Projects each source value to an Observable when Result/Either is `Left`
 */
export const mapLeft = <T extends { _tag: 'Left' | 'Right' }, L>(
  project: (value: Extract<T, { _tag: 'Left' }>, index: number) => L,
): OperatorFunction<T, Extract<T, { _tag: 'Right' }> | L> =>
  map((value, index) =>
    value._tag === 'Right'
      ? (value as Extract<T, { _tag: 'Right' }>)
      : project(value as Extract<T, { _tag: 'Left' }>, index),
  )

/**
 * Perform side effect without altering notification when emitted Result/Either is `Right`
 */
export const tapRight = <T extends { _tag: 'Left' | 'Right' }>(
  next: (value: Extract<T, { _tag: 'Right' }>) => void,
): MonoTypeOperatorFunction<T> =>
  tap((value) => (value._tag === 'Left' ? void 0 : next(value as Extract<T, { _tag: 'Right' }>)))

/**
 * Perform side effect without altering notification when emitted Result/Either is `Left`
 */
export const tapLeft = <T extends { _tag: 'Left' | 'Right' }>(
  next: (value: Extract<T, { _tag: 'Left' }>) => void,
): MonoTypeOperatorFunction<T> =>
  tap((value) => (value._tag === 'Right' ? void 0 : next(value as Extract<T, { _tag: 'Left' }>)))

/**
 * Applies a given `project` function to each value emitted by the source Observable
 * when emitted Result/Either is `Right`, and re-emits SUSPENSE if suspended
 */
export const mapSuspended = <T, R>(
  project: (value: T, index: number) => R,
): OperatorFunction<T | typeof SUSPENSE, R | typeof SUSPENSE> =>
  map((value, index) => (value === SUSPENSE ? SUSPENSE : project(value, index)))

/**
 * Applies a given `project` function to each value emitted by the source Observable
 * when emitted Result/Either is `Right`, and re-emits SUSPENSE if suspended
 */
export const mapRightSuspended = <T extends { _tag: 'Left' | 'Right' }, R>(
  project: (value: Extract<T, { _tag: 'Right' }>, index: number) => R,
): OperatorFunction<T | typeof SUSPENSE, Extract<T, { _tag: 'Left' }> | R | typeof SUSPENSE> =>
  mapSuspended((value, index) =>
    value._tag === 'Left'
      ? (value as Extract<T, { _tag: 'Left' }>)
      : project(value as Extract<T, { _tag: 'Right' }>, index),
  )

/**
 * Projects each source value to an Observable when value is not `SUSPENSE`. Re-emits `SUSPENSE`
 * values without projection.
 */
export const switchMapSuspended = <T, O extends ObservableInput<unknown>>(
  project: (value: T, index: number) => O,
): OperatorFunction<T | typeof SUSPENSE, ObservedValueOf<O> | typeof SUSPENSE> =>
  switchMap((value, index) => (value === SUSPENSE ? of(SUSPENSE) : project(value, index)))

/**
 * Filters out SUSPENSE values
 */
export const filterNotSuspended = filter(
  <T>(value: T | typeof SUSPENSE): value is T => value !== SUSPENSE,
)

/**
 * Filtering operator to take only the first event per key. If `keySelector` returns an empty key
 * (or `undefined`), `whenKeyEmpty` determines the behavior. `TAKE_IF_KEY_EMPTY` instructs the
 * operator to emit events if the key is empty. `SKIP_IF_KEY_EMPTY` discards events with an empty
 * key.
 */
export function takeOnePerKey<T>(
  keySelector: (item: T) => string | undefined,
  whenKeyEmpty: 'TAKE_IF_KEY_EMPTY' | 'SKIP_IF_KEY_EMPTY',
) {
  const takeIfKeyEmpty = whenKeyEmpty === 'TAKE_IF_KEY_EMPTY'
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      const witnessed = new Set<string>()
      const subscription = source$.subscribe({
        next(item) {
          const key = keySelector(item)
          if (!key) {
            if (takeIfKeyEmpty) {
              subscriber.next(item)
            }
          } else if (!witnessed.has(key)) {
            witnessed.add(key)
            subscriber.next(item)
          }
        },
        error(err) {
          subscriber.error(err)
        },
        complete() {
          subscriber.complete()
        },
      })
      return () => {
        subscription.unsubscribe()
      }
    })
}

/**
 * Operator that buffers until the `closingNotifier` emits once and then emits each buffered value
 * before continuing to emit each subsequent value without buffering.
 */
export function bufferUntil<T>(closingNotifier$: Observable<unknown>): MonoTypeOperatorFunction<T> {
  return (source$: Observable<T>) =>
    new Observable<T>((subscriber) => {
      let buffer: T[] | null = []
      const subscription = source$.subscribe({
        next(item) {
          // buffer until closingNotifier emits, which will set buffer to null
          if (buffer) {
            buffer.push(item)
          } else {
            subscriber.next(item)
          }
        },
        error(err) {
          subscriber.error(err)
        },
        complete() {
          if (!buffer) return // already completed
          // emit any items remaining in buffer
          for (const item of buffer) {
            subscriber.next(item)
          }
          buffer = null // disable further buffering
          subscriber.complete()
        },
      })

      const closingSubscription = closingNotifier$.subscribe({
        next() {
          if (!buffer) return // already completed
          // Emit all buffered items
          for (const item of buffer) {
            subscriber.next(item)
          }
          buffer = null // disable further buffering
        },
        error(err) {
          subscriber.error(err)
        },
        complete() {
          // no-op
        },
      })

      return () => {
        subscription.unsubscribe()
        closingSubscription.unsubscribe()
      }
    })
}

export interface RetryWithBackoffOptions {
  /** maximum number of retries (default 3) */
  count?: number
  /** ceiling for the exponential delay in milliseconds (default 30000) */
  maxDelayMs?: number
}

// exponential backoff capped at maxDelayMs; shared so the two variants don't drift
const backoffDelay = (retryCount: number, maxDelayMs: number) =>
  timer(Math.min(maxDelayMs, 2 ** retryCount * 1000))

export const retryWithBackoff = <T>({
  count = 3,
  maxDelayMs = 30000,
}: RetryWithBackoffOptions = {}) =>
  retry<T>({
    count,
    delay: (_err: unknown, retryCount) => backoffDelay(retryCount, maxDelayMs),
  })

export const retryWithBackoffWhen = <T>(
  /** retry only when the predicate returns true; otherwise re-throw the error */
  when: (err: unknown) => boolean,
  { count = 3, maxDelayMs = 30000 }: RetryWithBackoffOptions = {},
) =>
  retry<T>({
    count,
    delay: (err: unknown, retryCount) => {
      // re-throw error if predicate returns false
      if (!when(err)) {
        throw err
      }
      return backoffDelay(retryCount, maxDelayMs)
    },
  })
